import type {
  GenerationKind,
  GenerationOutput,
  GenerationRequest,
  ProviderCapabilities,
} from '../domain/schemas';

/**
 * Provider-neutral generation contracts.
 *
 * Phase 0 ships *no* remote or AI providers. Every remote capability is represented by
 * an `UnconfiguredProvider` that reports itself unavailable and refuses to generate.
 * Future phases plug real implementations into the same interface.
 */
export type ProviderAvailability =
  | { state: 'available' }
  | { state: 'unconfigured'; reason: string }
  | { state: 'disabled'; reason: string };

export interface GenerationResult {
  output: GenerationOutput;
  /** In-memory result too large or too structured for the output record (e.g. a composition). */
  payload?: unknown;
}

export interface GenerationProvider {
  readonly capabilities: ProviderCapabilities;
  availability(): ProviderAvailability;
  /**
   * Performs a generation. Implementations must validate the request against their
   * capabilities, honour `signal` when `supportsCancellation` is true, and throw a
   * `ProviderError` rather than returning placeholder/fake output.
   */
  generate(request: GenerationRequest, signal?: AbortSignal): Promise<GenerationResult>;
}

export type ProviderErrorCode = 'unavailable' | 'invalid-request' | 'cancelled' | 'failed';

export class ProviderError extends Error {
  readonly code: ProviderErrorCode;
  constructor(code: ProviderErrorCode, message: string) {
    super(message);
    this.name = 'ProviderError';
    this.code = code;
  }
}

export const GENERATION_KIND_LABELS: Record<GenerationKind, string> = {
  'text-to-music': 'Text to music',
  'text-to-audio': 'Text to audio / SFX',
  'text-to-video': 'Text to video',
  speech: 'Speech synthesis',
  procedural: 'Procedural (local)',
};
